import * as THREE from 'three';
import { CadThemeMode } from '../../eventBus';
import {
    isImportedSolidBodyDisplayMesh,
} from '../../imported-model/display/resolve-imported-visible-solid-body-material-recipe';
import {
    isFeatureResultMesh,
    type FeatureResultMesh,
} from '../../mesh/feature';


/**
 * 不着色模式实体材质名称。
 */
enum ModelDisplayUnshadedMaterialName {
    /** 不着色实体基础材质。 */
    Mesh = '模型显示模式-不着色实体材质',
}

/**
 * 不着色模式实体颜色。
 */
enum ModelDisplayUnshadedMeshColor {
    /** 亮色主题下的纯白实体。 */
    LightTheme = 0xffffff,
    /** 暗色主题下的纯黑实体。 */
    DarkTheme = 0x000000,
}

/**
 * 不着色材质快照。
 */
interface ModelDisplayUnshadedMaterialSnapshot {
    /** 被显示模式覆盖的实体网格。 */
    readonly mesh: THREE.Mesh;
    /** 进入不着色模式前的原始材质。 */
    readonly originalMaterial: THREE.Material | THREE.Material[];
    /** 当前显示模式创建并独占持有的材质。 */
    readonly unshadedMaterial: THREE.MeshBasicMaterial;
}

/**
 * 模型不着色显示模式材质控制器。
 *
 * 设计说明：
 * 1. 不着色模式仍保留实体面写深度，只把面材质替换为不受光照影响的 `MeshBasicMaterial`；
 * 2. 控制器只接管正式特征结果体和导入模型实体 mesh，不处理草图、基准、拓扑代理或 overlay；
 * 3. 原材质保存在控制器内部快照中，退出不着色模式或对象脱离场景时统一恢复并释放临时材质。
 */
export class ModelDisplayUnshadedMaterialController {
    /** 当前被不着色模式覆盖的材质快照。 */
    private readonly materialSnapshotMap = new Map<THREE.Mesh, ModelDisplayUnshadedMaterialSnapshot>();

    /**
     * 同步整场景不着色材质状态。
     *
     * @param root 当前 Viewer 主场景或需要同步的场景根节点。
     * @param enabled 是否启用不着色材质覆盖。
     * @param themeMode 当前主题模式。
     */
    public syncScene(
        root: THREE.Object3D,
        enabled: boolean,
        themeMode: CadThemeMode,
    ): void {
        if (!enabled) {
            this.restoreAll();
            return;
        }

        const activeMeshSet = new Set<THREE.Mesh>();
        root.traverse((object) => {
            if (!this.isUnshadedTargetMesh(object)) {
                return;
            }

            activeMeshSet.add(object);
            this.applyUnshadedMaterial(object, themeMode);
        });
        this.restoreDetachedMeshes(activeMeshSet);
    }

    /**
     * 同步单个正式特征结果体的不着色材质状态。
     *
     * @param mesh 正式特征结果体。
     * @param enabled 是否启用不着色材质覆盖。
     * @param themeMode 当前主题模式。
     */
    public syncFeatureResultMesh(
        mesh: FeatureResultMesh,
        enabled: boolean,
        themeMode: CadThemeMode,
    ): void {
        if (!enabled) {
            this.restoreMesh(mesh);
            return;
        }

        this.applyUnshadedMaterial(mesh, themeMode);
    }

    /**
     * 恢复单个 mesh 的原始材质。
     *
     * 设计说明：
     * 1. MeshManager 在删除或替换正式结果体前必须先调用该入口；
     * 2. 这样后续统一材质释放会释放原始材质，而不是只释放显示模式临时材质；
     * 3. 未被不着色模式接管的 mesh 调用该方法不会产生副作用。
     *
     * @param mesh 目标 mesh。
     */
    public restoreMeshMaterial(mesh: THREE.Mesh): void {
        this.restoreMesh(mesh);
    }

    /**
     * 释放控制器持有的全部临时材质并恢复原材质。
     */
    public dispose(): void {
        this.restoreAll();
    }

    /**
     * 判断对象是否应被不着色模式接管。
     *
     * @param object 场景对象。
     * @returns 应接管时返回 `true`。
     */
    private isUnshadedTargetMesh(object: THREE.Object3D): object is THREE.Mesh {
        return isFeatureResultMesh(object) || isImportedSolidBodyDisplayMesh(object);
    }

    /**
     * 应用不着色实体材质。
     *
     * @param mesh 目标实体 mesh。
     * @param themeMode 当前主题模式。
     */
    private applyUnshadedMaterial(mesh: THREE.Mesh, themeMode: CadThemeMode): void {
        const color = this.resolveMeshColor(themeMode);
        const snapshot = this.materialSnapshotMap.get(mesh);
        if (snapshot) {
            // 主题切换只需要更新颜色，不重建材质和几何。
            snapshot.unshadedMaterial.color.setHex(color);
            snapshot.unshadedMaterial.needsUpdate = true;
            if (mesh.material !== snapshot.unshadedMaterial) {
                mesh.material = snapshot.unshadedMaterial;
            }
            return;
        }

        const unshadedMaterial = this.createUnshadedMaterial(color);
        this.materialSnapshotMap.set(mesh, {
            mesh,
            originalMaterial: mesh.material,
            unshadedMaterial,
        });
        mesh.material = unshadedMaterial;
    }

    /**
     * 创建不着色实体材质。
     *
     * @param color 材质颜色。
     * @returns 不受光照影响的基础材质。
     */
    private createUnshadedMaterial(color: ModelDisplayUnshadedMeshColor): THREE.MeshBasicMaterial {
        const material = new THREE.MeshBasicMaterial({
            color,
            depthTest: true,
            depthWrite: true,
            side: THREE.DoubleSide,
        });
        material.name = ModelDisplayUnshadedMaterialName.Mesh;
        material.toneMapped = false;
        return material;
    }

    /**
     * 解析当前主题下的实体颜色。
     *
     * @param themeMode 当前主题模式。
     * @returns 实体颜色。
     */
    private resolveMeshColor(themeMode: CadThemeMode): ModelDisplayUnshadedMeshColor {
        return themeMode === CadThemeMode.Dark
            ? ModelDisplayUnshadedMeshColor.DarkTheme
            : ModelDisplayUnshadedMeshColor.LightTheme;
    }

    /**
     * 恢复已经脱离当前场景遍历结果的旧 mesh。
     *
     * @param activeMeshSet 本轮仍在场景中的目标 mesh 集合。
     */
    private restoreDetachedMeshes(activeMeshSet: ReadonlySet<THREE.Mesh>): void {
        Array.from(this.materialSnapshotMap.keys()).forEach((mesh) => {
            if (activeMeshSet.has(mesh)) {
                return;
            }

            this.restoreMesh(mesh);
        });
    }

    /**
     * 恢复单个 mesh 的原材质并释放临时材质。
     *
     * @param mesh 目标 mesh。
     */
    private restoreMesh(mesh: THREE.Mesh): void {
        const snapshot = this.materialSnapshotMap.get(mesh);
        if (!snapshot) {
            return;
        }

        snapshot.mesh.material = snapshot.originalMaterial;
        snapshot.unshadedMaterial.dispose();
        this.materialSnapshotMap.delete(mesh);
    }

    /**
     * 恢复全部被接管的 mesh。
     */
    private restoreAll(): void {
        Array.from(this.materialSnapshotMap.keys()).forEach((mesh) => {
            this.restoreMesh(mesh);
        });
    }
}

